/**
 * Voice Commands Hook
 * Hands-free control for audio-first experience
 */

import { useEffect, useRef, useState, useCallback } from 'react';

export type VoiceCommandType =
  | 'handle'
  | 'skip'
  | 'snooze'
  | 'repeat'
  | 'details'
  | 'pause'
  | 'resume'
  | 'approve'
  | 'reject'
  | 'next'
  | 'back';

interface VoiceCommandConfig {
  enabled: boolean;
  language?: string;
  continuous?: boolean;
}

export interface VoiceCommandResult {
  command: VoiceCommandType | null;
  confidence: number;
  transcript: string;
}

// Command patterns with variations
const COMMAND_PATTERNS: Record<VoiceCommandType, RegExp[]> = {
  handle: [/handle\s*(that|this|it)?/i, /approve/i, /accept/i, /yes/i, /do\s*it/i],
  skip: [/skip/i, /next/i, /pass/i, /move\s*on/i],
  snooze: [/snooze/i, /later/i, /defer/i, /remind\s*me/i],
  repeat: [/repeat/i, /again/i, /say\s*that\s*again/i, /what/i],
  details: [/details/i, /more/i, /expand/i, /tell\s*me\s*more/i],
  pause: [/pause/i, /stop/i, /wait/i, /hold\s*on/i],
  resume: [/resume/i, /continue/i, /play/i, /go\s*on/i],
  approve: [/approve/i, /accept/i, /confirm/i, /yes/i],
  reject: [/reject/i, /decline/i, /no/i, /deny/i],
  next: [/next/i, /forward/i],
  back: [/back/i, /previous/i, /go\s*back/i],
};

function parseCommand(transcript: string): VoiceCommandResult {
  const normalizedTranscript = transcript.toLowerCase().trim();

  for (const [command, patterns] of Object.entries(COMMAND_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern.test(normalizedTranscript)) {
        return {
          command: command as VoiceCommandType,
          confidence: 0.8,
          transcript: normalizedTranscript,
        };
      }
    }
  }

  return {
    command: null,
    confidence: 0,
    transcript: normalizedTranscript,
  };
}

export function useVoiceCommands(
  onCommand: (result: VoiceCommandResult) => void,
  config: VoiceCommandConfig = { enabled: false }
) {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInterface | null>(null);

  // Check browser support
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSupported(!!SpeechRecognition);
  }, []);

  // Initialize recognition
  useEffect(() => {
    if (!config.enabled || !isSupported) return;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = config.language || 'en-US';
    recognition.continuous = config.continuous ?? true;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onend = () => {
      setIsListening(false);
      if (config.continuous && config.enabled) {
        try {
          recognition.start();
        } catch {
          // Already started
        }
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech') return;
      setError(event.error);
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      const lastResult = event.results[event.results.length - 1];
      if (lastResult.isFinal) {
        const transcript = lastResult[0].transcript;
        const result = parseCommand(transcript);
        onCommand(result);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      recognition.stop();
      recognitionRef.current = null;
    };
  }, [config.enabled, config.language, config.continuous, isSupported, onCommand]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current || !config.enabled) return;

    try {
      recognitionRef.current.start();
    } catch {
      // Already started
    }
  }, [config.enabled]);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;

    recognitionRef.current.stop();
    setIsListening(false);
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    isSupported,
    error,
    startListening,
    stopListening,
    toggleListening,
  };
}

// Type declarations for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionInterface extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  onstart: ((this: SpeechRecognitionInterface, ev: Event) => void) | null;
  onend: ((this: SpeechRecognitionInterface, ev: Event) => void) | null;
  onerror: ((this: SpeechRecognitionInterface, ev: SpeechRecognitionErrorEvent) => void) | null;
  onresult: ((this: SpeechRecognitionInterface, ev: SpeechRecognitionEvent) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionConstructor {
  new(): SpeechRecognitionInterface;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

export default useVoiceCommands;
